import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Store, Truck, ChevronLeft } from "lucide-react";
import { useCart } from "../lib/cart";
import { shopApi, shopperAuth } from "../lib/shop-api";
import { money } from "../lib/api";

const FLAT = 15;
const FREE_OVER = 150;

export default function CheckoutPage() {
  const { items, subtotal, clear } = useCart();
  const nav = useNavigate();
  const info = shopperAuth.info();
  const [form, setForm] = useState({
    customer_name: info?.name || "",
    customer_email: info?.email || "",
    customer_phone: "",
    fulfillment: "pickup",
    notes: "",
    address: { name: "", line1: "", line2: "", city: "", state: "CA", zip: "" },
  });
  const [placed, setPlaced] = useState(null);
  const [saving, setSaving] = useState(false);

  const shipping = useMemo(() => {
    if (form.fulfillment === "pickup") return 0;
    return subtotal >= FREE_OVER ? 0 : FLAT;
  }, [form.fulfillment, subtotal]);
  const total = subtotal + shipping;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAddr = (k, v) => setForm((f) => ({ ...f, address: { ...f.address, [k]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    if (!form.customer_name || !form.customer_email) { toast.error("Nombre y correo requeridos"); return; }
    if (form.fulfillment === "ship" && (!form.address.line1 || !form.address.city || !form.address.zip)) {
      toast.error("Falta la dirección de envío"); return;
    }
    setSaving(true);
    try {
      const order = await shopApi.checkout({
        items: items.map((i) => ({ product_id: i.product_id, variant_id: i.variant_id || null, quantity: i.quantity })),
        fulfillment: form.fulfillment,
        shipping_address: form.fulfillment === "ship" ? form.address : null,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        notes: form.notes,
      });
      clear();
      setPlaced(order);
    } catch (err) {
      toast.error(err.message || "No se pudo crear el pedido");
    } finally {
      setSaving(false);
    }
  };

  if (placed) {
    return (
      <div className="min-h-screen bg-[#0A0908] text-[#F5F0E6] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-[#120F0D] border border-[#C89B3C]/40 rounded-2xl p-8 text-center" data-testid="checkout-success">
          <div className="w-14 h-14 rounded-full bg-[#C89B3C]/15 flex items-center justify-center mx-auto mb-4"><Check size={28} className="text-[#C89B3C]" /></div>
          <h2 className="text-3xl mb-2" style={{ fontFamily: "'Playfair Display'" }}>¡Pedido recibido!</h2>
          <p className="text-sm text-[#F5F0E6]/70 mb-1">Número de orden</p>
          <p className="text-xl font-mono text-[#C89B3C] mb-4" data-testid="checkout-order-number">{placed.order_number}</p>
          <p className="text-sm text-[#F5F0E6]/70 mb-6">
            {placed.fulfillment === "pickup"
              ? "Te avisaremos por correo cuando esté listo para recoger en 1270 Fourth St, Hollister CA."
              : "Te enviaremos un correo con el tracking cuando tu pedido salga."}
            {" "}El pago se procesa al confirmar en tienda o por teléfono.
          </p>
          <div className="flex gap-3">
            <Link to="/shop" className="flex-1 bg-white/5 border border-white/15 py-3 rounded-full text-sm">Seguir comprando</Link>
            <Link to="/shop/account" className="flex-1 bg-[#C89B3C] text-[#0A0908] font-bold py-3 rounded-full text-sm">Mis pedidos</Link>
          </div>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-[#0A0908] text-[#F5F0E6] flex items-center justify-center">
        <div className="text-center">
          <p className="mb-4 text-[#F5F0E6]/60">No tienes productos en el carrito.</p>
          <button onClick={() => nav("/shop")} className="bg-[#C89B3C] text-[#0A0908] font-bold px-6 py-3 rounded-full">Ir a la tienda</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0908] text-[#F5F0E6]">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        <Link to="/shop/cart" className="inline-flex items-center gap-1 text-sm text-[#F5F0E6]/60 hover:text-[#C89B3C] mb-4">
          <ChevronLeft size={16} /> Volver al carrito
        </Link>
        <h1 className="text-4xl md:text-5xl mb-8" style={{ fontFamily: "'Playfair Display'", fontWeight: 700 }}>Checkout</h1>

        <form onSubmit={submit} className="grid lg:grid-cols-[1fr_360px] gap-8">
          <div className="space-y-6">
            <section className="bg-[#120F0D] border border-white/10 rounded-xl p-6">
              <h2 className="text-xl mb-4" style={{ fontFamily: "'Playfair Display'" }}>Tus datos</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <input value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} placeholder="Nombre completo" className="bg-[#0A0908] border border-white/15 rounded px-3 py-2 text-sm" required data-testid="checkout-name" />
                <input value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} placeholder="Correo electrónico" type="email" className="bg-[#0A0908] border border-white/15 rounded px-3 py-2 text-sm" required data-testid="checkout-email" />
                <input value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} placeholder="Teléfono" className="bg-[#0A0908] border border-white/15 rounded px-3 py-2 text-sm md:col-span-2" data-testid="checkout-phone" />
              </div>
            </section>

            <section className="bg-[#120F0D] border border-white/10 rounded-xl p-6">
              <h2 className="text-xl mb-4" style={{ fontFamily: "'Playfair Display'" }}>Entrega</h2>
              <div className="grid md:grid-cols-2 gap-3 mb-4" data-testid="checkout-fulfillment">
                <label className={`cursor-pointer p-4 rounded-xl border transition-colors flex items-center gap-3 ${form.fulfillment === "pickup" ? "border-[#C89B3C] bg-[#C89B3C]/5" : "border-white/15 hover:border-white/30"}`}>
                  <input type="radio" name="f" checked={form.fulfillment === "pickup"} onChange={() => set("fulfillment", "pickup")} className="sr-only" data-testid="checkout-pickup" />
                  <Store size={20} className="text-[#C89B3C]" />
                  <div className="text-sm">
                    <p className="font-semibold">Pickup en tienda</p>
                    <p className="text-[#F5F0E6]/60 text-xs">Hollister, CA · Gratis</p>
                  </div>
                </label>
                <label className={`cursor-pointer p-4 rounded-xl border transition-colors flex items-center gap-3 ${form.fulfillment === "ship" ? "border-[#C89B3C] bg-[#C89B3C]/5" : "border-white/15 hover:border-white/30"}`}>
                  <input type="radio" name="f" checked={form.fulfillment === "ship"} onChange={() => set("fulfillment", "ship")} className="sr-only" data-testid="checkout-ship" />
                  <Truck size={20} className="text-[#C89B3C]" />
                  <div className="text-sm">
                    <p className="font-semibold">Envío a domicilio</p>
                    <p className="text-[#F5F0E6]/60 text-xs">$15 · Gratis sobre $150</p>
                  </div>
                </label>
              </div>

              {form.fulfillment === "ship" && (
                <div className="grid md:grid-cols-2 gap-3" data-testid="checkout-address">
                  <input value={form.address.line1} onChange={(e) => setAddr("line1", e.target.value)} placeholder="Dirección" className="bg-[#0A0908] border border-white/15 rounded px-3 py-2 text-sm md:col-span-2" required data-testid="checkout-addr-line1" />
                  <input value={form.address.line2} onChange={(e) => setAddr("line2", e.target.value)} placeholder="Apt / Ste (opcional)" className="bg-[#0A0908] border border-white/15 rounded px-3 py-2 text-sm md:col-span-2" />
                  <input value={form.address.city} onChange={(e) => setAddr("city", e.target.value)} placeholder="Ciudad" className="bg-[#0A0908] border border-white/15 rounded px-3 py-2 text-sm" required data-testid="checkout-city" />
                  <input value={form.address.state} onChange={(e) => setAddr("state", e.target.value)} placeholder="Estado" className="bg-[#0A0908] border border-white/15 rounded px-3 py-2 text-sm" required data-testid="checkout-state" />
                  <input value={form.address.zip} onChange={(e) => setAddr("zip", e.target.value)} placeholder="ZIP" className="bg-[#0A0908] border border-white/15 rounded px-3 py-2 text-sm" required data-testid="checkout-zip" />
                </div>
              )}

              <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Notas para la tienda (opcional)" className="w-full mt-4 bg-[#0A0908] border border-white/15 rounded px-3 py-2 text-sm" rows={2} />
            </section>

            <section className="bg-[#120F0D] border border-white/10 rounded-xl p-6">
              <h2 className="text-xl mb-2" style={{ fontFamily: "'Playfair Display'" }}>Pago</h2>
              <p className="text-sm text-[#F5F0E6]/70">
                Confirmas tu pedido ahora y pagas al <strong className="text-[#C89B3C]">recoger en tienda</strong> o te contactamos para pago por teléfono. Guardamos tu talla y color: la mercancía queda reservada para ti.
              </p>
            </section>
          </div>

          <aside className="bg-[#120F0D] border border-white/10 rounded-xl p-6 h-fit lg:sticky lg:top-24">
            <h2 className="text-xl mb-4" style={{ fontFamily: "'Playfair Display'" }}>Resumen</h2>
            <ul className="space-y-2 mb-4 max-h-60 overflow-y-auto">
              {items.map((i) => (
                <li key={`${i.product_id}-${i.variant_id || "x"}`} className="flex justify-between text-sm">
                  <span className="text-[#F5F0E6]/80 truncate pr-2">{i.name} <span className="text-[#F5F0E6]/40">× {i.quantity}</span></span>
                  <span>{money(i.price * i.quantity)}</span>
                </li>
              ))}
            </ul>
            <div className="border-t border-white/10 pt-3 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[#F5F0E6]/70">Subtotal</span><span>{money(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-[#F5F0E6]/70">Envío</span><span>{money(shipping)}</span></div>
            </div>
            <div className="flex justify-between font-bold text-lg mt-3 pt-3 border-t border-white/10">
              <span>Total</span><span data-testid="checkout-total">{money(total)}</span>
            </div>
            <button type="submit" disabled={saving} className="w-full mt-4 bg-[#C89B3C] hover:bg-[#E6BF5F] disabled:opacity-60 text-[#0A0908] font-bold uppercase tracking-wider py-3 rounded-full" data-testid="checkout-submit">
              {saving ? "Procesando…" : "Confirmar pedido"}
            </button>
          </aside>
        </form>
      </div>
    </div>
  );
}
